import { isValid } from 'iban'
import * as Yup from 'yup'

import { REG_EXP_BIC } from '../../../constants/regExp'
import { WEBAPP_ROUTE_PREFIX } from '../../../constants/route'
import i18n from '../../../i18n'
import { getItemsIds } from '../../../utils/common'
import { formatDateToApi } from '../../../utils/date'
import { clearEmptyFields } from '../../../utils/formHelpers'
import { getBuildingProfilePathname } from '../../../utils/link'
import { getAddressSelectFieldWithCreationValidationSchema } from '../../Address'

import {
  BUILDING_FORM_ADDRESS_FIELD_MAX_LENGTH,
  BUILDING_FORM_FIELD_NAME_ADDRESS,
  BUILDING_FORM_FIELD_NAME_AVATAR,
  BUILDING_FORM_FIELD_NAME_BANK_NAME,
  BUILDING_FORM_FIELD_NAME_BANK_OWNER,
  BUILDING_FORM_FIELD_NAME_BIC,
  BUILDING_FORM_FIELD_NAME_BUILDING_GROUP,
  BUILDING_FORM_FIELD_NAME_BUILDING_NUMBER,
  BUILDING_FORM_FIELD_NAME_CONTRACT_START,
  BUILDING_FORM_FIELD_NAME_FILES,
  BUILDING_FORM_FIELD_NAME_IBAN,
  BUILDING_FORM_FIELD_NAME_INSURANCES,
  BUILDING_FORM_FIELD_NAME_NOTES,
  BUILDING_FORM_FIELD_NAME_RESPONSIBLE_MANAGER,
  BUILDING_FORM_FIELD_NAME_TYPE_OF_BUILDING,
  BUILDING_FORM_FIELD_NAME_UPDATE_MANAGER_FOR_ALL_BUILDINGS_IN_GROUP,
  BUILDING_FORM_INSURANCE_CONTRACT_NUMBER_FIELD_MAX_LENGTH,
  BUILDING_FORM_INSURANCE_FIELD_NAME_CONTRACT_NUMBER,
  BUILDING_FORM_INSURANCE_FIELD_NAME_INSURANCE_TYPE,
  BUILDING_FORM_INSURANCE_FIELD_NAME_SUPPLIER,
  BUILDING_FORM_NOTES_FIELD_MAX_LENGTH,
} from './BuildingForm.constants'

function getBankDetailsPayload(values) {
  return {
    [BUILDING_FORM_FIELD_NAME_BANK_NAME]:
      values[BUILDING_FORM_FIELD_NAME_BANK_NAME],
    [BUILDING_FORM_FIELD_NAME_BANK_OWNER]:
      values[BUILDING_FORM_FIELD_NAME_BANK_OWNER],
    [BUILDING_FORM_FIELD_NAME_BIC]: values[BUILDING_FORM_FIELD_NAME_BIC],
    [BUILDING_FORM_FIELD_NAME_IBAN]: values[BUILDING_FORM_FIELD_NAME_IBAN],
  }
}

function getInsurancePayload(values) {
  const insurance = values[0]

  return {
    [BUILDING_FORM_INSURANCE_FIELD_NAME_CONTRACT_NUMBER]:
      insurance[BUILDING_FORM_INSURANCE_FIELD_NAME_CONTRACT_NUMBER] ?? null,
    [BUILDING_FORM_INSURANCE_FIELD_NAME_INSURANCE_TYPE]:
      insurance[BUILDING_FORM_INSURANCE_FIELD_NAME_INSURANCE_TYPE]?.value ??
      null,
    [BUILDING_FORM_INSURANCE_FIELD_NAME_SUPPLIER]:
      values[BUILDING_FORM_INSURANCE_FIELD_NAME_SUPPLIER]?.value ?? null,
  }
}

export function getBuildingFormValidationSchema() {
  return Yup.object({
    [BUILDING_FORM_FIELD_NAME_ADDRESS]:
      getAddressSelectFieldWithCreationValidationSchema({
        maxLength: BUILDING_FORM_ADDRESS_FIELD_MAX_LENGTH,
        required: true,
      }),
    [BUILDING_FORM_FIELD_NAME_BIC]: Yup.string()
      .matches(REG_EXP_BIC, i18n.t('Error.IncorrectBICKey'))
      .nullable(),
    [BUILDING_FORM_FIELD_NAME_IBAN]: Yup.string().test({
      message: i18n.t('Error.IncorrectIBANKey'),
      test: value => !value || isValid(value),
    }),
    [BUILDING_FORM_FIELD_NAME_INSURANCES]: Yup.array().of(
      Yup.object().shape({
        [BUILDING_FORM_INSURANCE_FIELD_NAME_CONTRACT_NUMBER]: Yup.string().max(
          BUILDING_FORM_INSURANCE_CONTRACT_NUMBER_FIELD_MAX_LENGTH,
          i18n.t('Error.MaxLengthKey', {
            length: BUILDING_FORM_INSURANCE_CONTRACT_NUMBER_FIELD_MAX_LENGTH,
          })
        ),
        [BUILDING_FORM_INSURANCE_FIELD_NAME_INSURANCE_TYPE]: Yup.object({
          value: Yup.number(),
        })
          .nullable()
          .typeError(i18n.t('Error.IncorrectValueKey')),

        [BUILDING_FORM_INSURANCE_FIELD_NAME_SUPPLIER]: Yup.object({
          value: Yup.number(),
        })
          .nullable()
          .typeError(i18n.t('Error.IncorrectValueKey')),
      })
    ),
    [BUILDING_FORM_FIELD_NAME_NOTES]: Yup.string()
      .nullable()
      .max(
        BUILDING_FORM_NOTES_FIELD_MAX_LENGTH,
        i18n.t('Error.MaxLengthKey', {
          length: BUILDING_FORM_NOTES_FIELD_MAX_LENGTH,
        })
      ),
    [BUILDING_FORM_FIELD_NAME_TYPE_OF_BUILDING]: Yup.object({
      value: Yup.number(),
    })
      .nullable()
      .typeError(i18n.t('Error.IncorrectValueKey')),
  })
}

export function getBuildingFormCreationPayload({ files, timezone, values }) {
  const payload = {
    [BUILDING_FORM_FIELD_NAME_AVATAR]: values[BUILDING_FORM_FIELD_NAME_AVATAR],
    [BUILDING_FORM_FIELD_NAME_BUILDING_GROUP]: getItemsIds(
      values[BUILDING_FORM_FIELD_NAME_BUILDING_GROUP],
      'value'
    ),
    [BUILDING_FORM_FIELD_NAME_BUILDING_NUMBER]:
      values[BUILDING_FORM_FIELD_NAME_BUILDING_NUMBER] ?? null,
    [BUILDING_FORM_FIELD_NAME_NOTES]:
      values[BUILDING_FORM_FIELD_NAME_NOTES] ?? null,
    [BUILDING_FORM_FIELD_NAME_RESPONSIBLE_MANAGER]: getItemsIds(
      values[BUILDING_FORM_FIELD_NAME_RESPONSIBLE_MANAGER],
      'value'
    ),
    [BUILDING_FORM_FIELD_NAME_TYPE_OF_BUILDING]:
      values[BUILDING_FORM_FIELD_NAME_TYPE_OF_BUILDING]?.value ?? null,
    [BUILDING_FORM_FIELD_NAME_UPDATE_MANAGER_FOR_ALL_BUILDINGS_IN_GROUP]:
      values[
        BUILDING_FORM_FIELD_NAME_UPDATE_MANAGER_FOR_ALL_BUILDINGS_IN_GROUP
      ],
    bank_details: clearEmptyFields(getBankDetailsPayload(values)),
  }

  if (values[BUILDING_FORM_FIELD_NAME_CONTRACT_START]) {
    payload[BUILDING_FORM_FIELD_NAME_CONTRACT_START] = formatDateToApi(
      values[BUILDING_FORM_FIELD_NAME_CONTRACT_START],
      timezone
    )
  }

  if (files.length > 0) {
    payload[BUILDING_FORM_FIELD_NAME_FILES] = getItemsIds(files)
  }

  if (values[BUILDING_FORM_FIELD_NAME_INSURANCES]?.length > 0) {
    payload[BUILDING_FORM_FIELD_NAME_INSURANCES] = clearEmptyFields(
      getInsurancePayload(values[BUILDING_FORM_FIELD_NAME_INSURANCES])
    )
  }

  return payload
}

export function getBuildingFormSuccessNotification({
  address,
  availableSections,
  id,
  objectNumber,
}) {
  const link = getBuildingProfilePathname({ availableSections, id })

  return {
    linkProps: {
      // Webapp fallback needs a full document load; NG profile navigates in-app.
      reloadDocument: link.includes(WEBAPP_ROUTE_PREFIX),
      title: i18n.t('Notification.BuildingCreatedViewBuildingLinkKey'),
      to: link,
    },
    text: i18n.t('Notification.BuildingCreatedTextKey', {
      address,
      objectNumber,
    }),
    title: i18n.t('Notification.BuildingCreatedTitleKey'),
  }
}

export function getBuildingFormErrorNotification() {
  return {
    text: i18n.t('Notification.BuildingCreationErrorTextKey'),
    title: i18n.t('Notification.BuildingCreationErrorTitleKey'),
  }
}
